
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FaBuilding,
  FaCheckCircle,
  FaExchangeAlt,
  FaMoneyBillWave,
  FaStore,
  FaUserTie,
} from "react-icons/fa";
import { Chart, registerables } from "chart.js";
import "../styles/Dashboard.css";
import { getOrgDashboardStats } from "../api/api";
import { getUserData } from "../utils/authStorage";

Chart.register(...registerables);

const LABELS = {
  today: ["08:00", "10:00", "12:00", "14:00", "16:00", "18:00", "20:00", "22:00"],
  weekly: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  monthly: ["W1", "W2", "W3", "W4"],
  custom: ["D1", "D2", "D3", "D4", "D5", "D6", "D7"],
};

const sum = (arr) => arr.reduce((a, b) => a + (Number(b) || 0), 0);

const FALLBACK_STATS = {
  totalMerchants: 0,
  activeMerchants: 0,
  totalAgents: 0,
  successRate: 0,
  pending: 0,
  dynamicQrShare: 0,
  staticQrShare: 0,
};

function normalizeSeries(range, raw) {
  const labels = LABELS[range] || LABELS.today;
  const arr = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
  const mapped = arr
    .map((d) => ({
      label: String(d?.label ?? d?.day ?? d?.date ?? d?.name ?? ""),
      count: Number(d?.count ?? d?.transactions ?? d?.totalTransactions ?? 0),
      amount: Number(d?.amount ?? d?.volume ?? d?.totalAmount ?? 0),
    }))
    .filter((d) => d.label);

  const byLabel = new Map(mapped.map((d) => [d.label, d]));
  return labels.map((l) => byLabel.get(l) || { label: l, count: 0, amount: 0 });
}

/* ── dark-mode hook ── */
function useDark() {
  const [dark, setDark] = useState(window.matchMedia("(prefers-color-scheme:dark)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme:dark)");
    const h = (e) => setDark(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return dark;
}

/* ── generic Chart.js wrapper ── */
function CJSChart({ id, build }) {
  const ref = useRef(null);
  const inst = useRef(null);
  const dark = useDark();

  useEffect(() => {
    if (!ref.current) return;
    inst.current?.destroy();
    inst.current = build(ref.current, dark);
    return () => inst.current?.destroy();
  }, [dark, build]);

  return (
    <div style={{ position: "relative", width: "100%", height: 150 }}>
      <canvas ref={ref} id={id} />
    </div>
  );
}

/* ── stat card ── */
const StatTile = ({ label, value, icon, accent, trend, progress }) => (
  <div className="msd-kpi">
    <div className="msd-kpi__head">
      <div className="msd-kpi__label">{label}</div>
      <div className="msd-kpi__icon" style={{ background: accent + "25", color: accent }}>
        {icon}
      </div>
    </div>
    <div className="msd-kpi__value">{value?.toLocaleString?.() ?? value ?? 0}</div>
    {trend ? <div className="msd-kpi__trend">{trend}</div> : null}
    {typeof progress === "number" ? (
      <div className="msd-kpi__progress">
        <div className="msd-kpi__progress-bar" style={{ width: `${progress}%`, background: accent }} />
      </div>
    ) : null}
    <div className="msd-kpi__spark" style={{ background: accent + "55" }} />
  </div>
);

/* ══════════════════════════════════════════ */
const OrganizationDashboard = ({ isSidebarOpen }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("today");
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const userData = getUserData();
  const user = userData?.user || userData?.data || userData || {};
  const orgId = user?.profile?.orgId || user?.orgId || user?.organizationId || null;

  useEffect(() => {
    const fetchData = async () => {
      if (!orgId) {
        setStats(FALLBACK_STATS);
        setLoading(false);
        return;
      }
      try {
        const res = await getOrgDashboardStats(orgId);
        if (res?.data) setStats(res.data);
        else setStats(FALLBACK_STATS);
      } catch (err) {
        console.error("Error fetching stats:", err);
        setStats(FALLBACK_STATS);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orgId]);

  const fetchCharts = useCallback(async (f) => {
    setChartLoading(true);
    try {
      const res = await getOrgDashboardStats(orgId);
      const raw = res?.data?.series ?? res?.data?.chart ?? res?.data?.charts ?? res?.data?.data ?? null;
      setChartData(normalizeSeries(f, raw));
    } catch {
      setChartData(normalizeSeries(f, null));
    } finally {
      setChartLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchCharts(filter);
  }, [filter, fetchCharts]);

  if (loading) {
    return (
      <div className={`msd-container ${isSidebarOpen ? "shifted" : ""}`}>
        <p className="msd-muted">Loading dashboard…</p>
      </div>
    );
  }
  const safeStats = stats || FALLBACK_STATS;

  const pending = Number(safeStats?.pending ?? 0);
  const successRate = Number(safeStats?.successRate ?? 0);
  const dynamicShare = Number(safeStats?.dynamicQrShare ?? 0);
  const staticShare = Number(safeStats?.staticQrShare ?? 0);

  const rangeLabels = LABELS[filter] || LABELS.today;
  const safeChartData = chartData.length
    ? chartData
    : rangeLabels.map((label) => ({
        label,
        count: 0,
        amount: 0,
      }));

  const labels = safeChartData.map((d) => d.label);
  const counts = safeChartData.map((d) => d.count);
  const amounts = safeChartData.map((d) => d.amount);
  const totalAmount = sum(amounts);

  const kpis = [
    { label: "Total Merchants", value: safeStats?.totalMerchants ?? 0, icon: <FaStore />, accent: "#44EDCC", trend: "0%" },
    { label: "Total Agents", value: safeStats?.totalAgents ?? 0, icon: <FaUserTie />, accent: "#B6C4FF", trend: "0%" },
    { label: "Volume", value: totalAmount ? `Rs ${Math.round(totalAmount / 1000) / 100}M` : "Rs 0", icon: <FaExchangeAlt />, accent: "#0057FF", trend: "0%" },
    { label: "Revenue", value: totalAmount ? `Rs ${Math.round(totalAmount * 0.038 / 1000)}k` : "Rs 0", icon: <FaMoneyBillWave />, accent: "#FBBF24", trend: "0%" },
    { label: "Success Rate", value: `${successRate}%`, icon: <FaCheckCircle />, accent: "#44EDCC", progress: Math.max(0, Math.min(100, successRate)) },
    { label: "Pending", value: pending, icon: <FaBuilding />, accent: "rgba(255,255,255,0.7)", trend: "Next settlement in 4h" },
  ];

  const topAgents = [
    { initials: "JD", name: "James D. Wilson", sub: "Northwest Region", value: "Rs 0", delta: "0%" },
    { initials: "ML", name: "Maria Lopez", sub: "South Sector", value: "Rs 0", delta: "0%" },
  ];

  const topMerchants = [
    { name: "GearHub Electronics", sub: "Tier 1 Merchant", value: "Rs 0", foot: "High Velocity" },
    { name: "Urban Brew Collective", sub: "Tier 2 Merchant", value: "Rs 0", foot: "Stable Flow" },
  ];

  const approvals = [
    { name: "Zenith Retail Group", sub: "Merchant Application • High Risk" },
    { name: "K. Richards", sub: "Agent Commission Increase • 0%" },
  ];

  const agentActivity = [
    { dot: "#44edcc", name: "Sarah Connor", sub: 'Onboarded "Atlas Bakery" • 10m ago' },
    { dot: "#a78bfa", name: "Robert Fox", sub: "Resolved Settlement Issue #450 • 1h ago" },
    { dot: "rgba(255,255,255,0.55)", name: "System", sub: "Weekly Report Generated • 4h ago" },
  ];

  const transactions = [
    { time: "14:24:05", merchant: "Aura Wellness Spa", method: "Dynamic QR", amount: "Rs 0", status: "SUCCESS" },
    { time: "14:22:18", merchant: "The Daily Grind", method: "Static QR", amount: "Rs 0", status: "SUCCESS" },
    { time: "14:18:44", merchant: "Swift Logistics", method: "Payment Link", amount: "Rs 0", status: "PENDING" },
  ];

  const heatmap = [
    { label: "Jan", v: 0.25 }, { label: "Feb", v: 0.30 }, { label: "Mar", v: 0.34 }, { label: "Apr", v: 0.42 },
    { label: "May", v: 0.55 }, { label: "Jun", v: 0.48 }, { label: "Jul", v: 0.40 }, { label: "Aug", v: 0.36 },
    { label: "Sep", v: 0.62 }, { label: "Oct", v: 0.58 }, { label: "Nov", v: 0.45 }, { label: "Dec", v: 0.52 },
  ];

  const buildTrend = (canvas, dark) => {
    const grid = dark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.06)";
    const tick = dark ? "rgba(255,255,255,.42)" : "#888";
    return new Chart(canvas, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            data: counts,
            borderColor: "#0057FF",
            borderWidth: 3,
            pointRadius: 0,
            pointHoverRadius: 5,
            fill: true,
            backgroundColor: (ctx) => {
              const g = ctx.chart.ctx.createLinearGradient(0, 0, 0, 220);
              g.addColorStop(0, "rgba(0,87,255,0.22)");
              g.addColorStop(1, "rgba(0,87,255,0.00)");
              return g;
            },
            tension: 0.45,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { grid: { display: false }, ticks: { color: tick, font: { size: 10 } } },
          y: { grid: { color: grid }, ticks: { color: tick, font: { size: 10 } } },
        },
      },
    });
  };

  const buildGrowth = (canvas, dark) => {
    const tick = dark ? "rgba(255,255,255,.42)" : "#888";
    const maxVal = Math.max(...counts.map((v) => Number(v) || 0), 0);
    const colors = counts.map((v) => (Number(v) === maxVal ? "#44EDCC" : "rgba(255,255,255,0.10)"));
    return new Chart(canvas, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            data: counts,
            backgroundColor: colors,
            borderRadius: 10,
            borderSkipped: false,
            barPercentage: 0.65,
            categoryPercentage: 0.7,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { grid: { display: false }, ticks: { color: tick, font: { size: 10 } } },
          y: { grid: { display: false }, ticks: { display: false } },
        },
      },
    });
  };

  const buildQrSplit = (canvas, dark) => {
    const border = dark ? "rgba(5,8,22,0.92)" : "#fff";
    return new Chart(canvas, {
      type: "doughnut",
      data: {
        labels: ["Dynamic QR", "Static QR"],
        datasets: [
          {
            data: [dynamicShare, staticShare],
            backgroundColor: ["#FBBF24", "#A78BFA"],
            borderColor: border,
            borderWidth: 3,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "78%",
        plugins: { legend: { display: false } },
      },
    });
  };

  return (
    <div className={`msd-container ${isSidebarOpen ? "shifted" : ""}`}>
      <div className="msd-kpis msd-kpis--admin">
        {kpis.map((t) => (
          <StatTile
            key={t.label}
            label={t.label}
            value={t.value}
            icon={t.icon}
            accent={t.accent}
            trend={t.trend}
            progress={t.progress}
          />
        ))}
      </div>

      <div className="msd-admingrid">
        <div className="msd-admingrid__main">
          <div className="msd-admingrid__row3">
            <div className="msd-card msd-card--wide">
              <div className="msd-card__head">
                <div>
                  <div className="msd-card__title">Transaction Trend</div>
                  <div className="msd-card__subtitle">Real-time throughput</div>
                </div>
              </div>
              <div className="msd-card__body">
                {chartLoading ? <p className="msd-muted">Loading…</p> : <CJSChart id="org-trend" build={buildTrend} />}
              </div>
            </div>

            <div className="msd-card msd-card--wide">
              <div className="msd-card__head">
                <div>
                  <div className="msd-card__title">Merchant Growth</div>
                  <div className="msd-card__subtitle">New onboardings</div>
                </div>
              </div>
              <div className="msd-card__body">
                <CJSChart id="org-growth" build={buildGrowth} />
              </div>
            </div>

            <div className="msd-card msd-card--wide">
              <div className="msd-card__head">
                <div>
                  <div className="msd-card__title">QR Usage Split</div>
                </div>
              </div>
              <div className="msd-card__body msd-adminqr">
                <div className="msd-adminqr__ring">
                  <CJSChart id="org-qr" build={buildQrSplit} />
                  <div className="msd-adminqr__center">
                    <div className="msd-adminqr__pct">{dynamicShare}%</div>
                    <div className="msd-adminqr__label">Dynamic</div>
                  </div>
                </div>
                <div className="msd-adminqr__legend">
                  <div className="msd-legend-row"><span className="msd-dot" style={{ background: "#FBBF24" }} />Dynamic QR<span className="msd-legend-val">Rs 0</span></div>
                  <div className="msd-legend-row"><span className="msd-dot" style={{ background: "#A78BFA" }} />Static QR<span className="msd-legend-val">Rs 0</span></div>
                </div>
              </div>
            </div>
          </div>

          <div className="msd-admingrid__row2">
            <div className="msd-card">
              <div className="msd-card__head">
                <div>
                  <div className="msd-card__title">Top Agents</div>
                </div>
              </div>
              <div className="msd-list">
                {topAgents.map((a) => (
                  <div key={a.name} className="msd-listrow">
                    <div className="msd-listrow__left">
                      <div className="msd-chip">{a.initials}</div>
                      <div>
                        <div className="msd-listrow__title">{a.name}</div>
                        <div className="msd-listrow__sub">{a.sub}</div>
                      </div>
                    </div>
                    <div className="msd-listrow__right">
                      <div className="msd-listrow__value">{a.value}</div>
                      <div className="msd-listrow__delta">{a.delta}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="msd-card">
              <div className="msd-card__head">
                <div>
                  <div className="msd-card__title">Top Merchants</div>
                </div>
              </div>
              <div className="msd-list">
                {topMerchants.map((m) => (
                  <div key={m.name} className="msd-listrow">
                    <div className="msd-listrow__left">
                      <div className="msd-chip msd-chip--dark" />
                      <div>
                        <div className="msd-listrow__title">{m.name}</div>
                        <div className="msd-listrow__sub">{m.sub}</div>
                      </div>
                    </div>
                    <div className="msd-listrow__right">
                      <div className="msd-listrow__value">{m.value}</div>
                      <div className="msd-listrow__sub">{m.foot}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="msd-card msd-card--wide">
              <div className="msd-card__head">
                <div>
                  <div className="msd-card__title">Recent Transactions</div>
                </div>
                <button type="button" className="msd-linkbtn">View All</button>
              </div>
              <div className="msd-txntable">
                <div className="msd-txntable__head">
                  <div>Timestamp</div>
                  <div>Merchant</div>
                  <div>Method</div>
                  <div>Amount</div>
                  <div>Status</div>
                </div>
                {transactions.map((t) => (
                  <div key={t.time + t.merchant} className="msd-txntable__row">
                    <div className="msd-mono">{t.time}</div>
                    <div className="msd-txntable__merchant">{t.merchant}</div>
                    <div className="msd-txntable__method">{t.method}</div>
                    <div className="msd-mono">{t.amount}</div>
                    <div>
                      <span className={t.status === "SUCCESS" ? "msd-pill msd-pill--success" : "msd-pill msd-pill--pending"}>
                        {t.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="msd-admingrid__row2 msd-admingrid__row2--bottom">
            <div className="msd-card">
              <div className="msd-card__head">
                <div>
                  <div className="msd-card__title">Approvals</div>
                </div>
                <span className="msd-badge msd-badge--review">5 NEW</span>
              </div>
              <div className="msd-approvals">
                {approvals.map((a) => (
                  <div key={a.name} className="msd-approval">
                    <div>
                      <div className="msd-approval__title">{a.name}</div>
                      <div className="msd-approval__sub">{a.sub}</div>
                    </div>
                    <div className="msd-approval__actions">
                      <button type="button" className="msd-approvebtn">Approve</button>
                      <button type="button" className="msd-ghostbtn">Details</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="msd-card">
              <div className="msd-card__head">
                <div>
                  <div className="msd-card__title">Agent Activity</div>
                </div>
                <button type="button" className="msd-fab" aria-label="Add">+</button>
              </div>
              <div className="msd-activitylist">
                {agentActivity.map((a) => (
                  <div key={a.name} className="msd-activityitem">
                    <span className="msd-activitydot" style={{ background: a.dot }} />
                    <div>
                      <div className="msd-activityitem__title">{a.name}</div>
                      <div className="msd-activityitem__sub">{a.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="msd-admingrid__row2 msd-admingrid__row2--bottom">
            <div className="msd-card msd-card--wide">
              <div className="msd-card__head">
                <div>
                  <div className="msd-card__title">Monthly Revenue Heatmap</div>
                </div>
              </div>
              <div className="msd-heatmap">
                {heatmap.map((m) => (
                  <div key={m.label} className="msd-heatmap__cell">
                    <div className="msd-heatmap__box" style={{ opacity: 0.25 + m.v }} />
                    <div className="msd-heatmap__label">{m.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="msd-card">
              <div className="msd-card__head">
                <div>
                  <div className="msd-card__title">Settlement Progress</div>
                </div>
              </div>
              <div className="msd-steps">
                {["Calculated", "Verified", "Processing", "Settled"].map((s, idx) => (
                  <div key={s} className={idx < 3 ? "msd-step active" : "msd-step"}>
                    <div className="msd-step__dot" />
                    <div className="msd-step__label">{s}</div>
                  </div>
                ))}
              </div>
              <div className="msd-nextbatch">
                <div className="msd-nextbatch__label">Next Batch</div>
                <div className="msd-nextbatch__value">Rs 0</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationDashboard;
